import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
// UI coponents
import { Text } from "@mantine/core";
// styles
import css from "./password-requirements.module.css";
import { clsx } from "clsx";
// types
import type { TFunction } from "i18next";

/**
 * TYPES
 */

export interface IPasswordRequirement {
  text: string;
  isMet: boolean;
}

/**
 * HELPER FUNCTIONS
 *
 * these are exported so they can be used in the `useForm({validate: {password: (value) => ...}})` function
 * which doesn't happen within react rendering engine
 */

export const calculateIfPasswordMeetsRequirements = (
  passwordValue: string,
  t: TFunction,
): IPasswordRequirement[] => {
  const requirements: IPasswordRequirement[] = [];

  requirements.push({
    text: t("startPage.validation.passwordRequired"),
    isMet: !!passwordValue,
  });

  requirements.push({
    text: t("startPage.inputs.requirement1NumbeOfChars"),
    isMet: passwordValue.length >= 8 && passwordValue.length <= 40,
  });

  requirements.push({
    text: t("startPage.inputs.requirement2Lowercase"),
    isMet: /[a-z]/.test(passwordValue),
  });

  requirements.push({
    text: t("startPage.inputs.requirement3Uppercase"),
    isMet: /[A-Z]/.test(passwordValue),
  });

  requirements.push({
    text: t("startPage.inputs.requirement4Number"),
    isMet: /[0-9]/.test(passwordValue),
  });

  requirements.push({
    text: t("startPage.inputs.requirement5SpecialCharacter"),
    isMet: /[~!@#$%^&*()_+\-=[\]{};:,./<>?]/.test(passwordValue),
  });

  requirements.push({
    text: t("startPage.validation.passwordDisallowedChars", {
      allowedSymbols: "~!@#$%^&*()_+-=[]{};:,./<>?",
    }),
    isMet: /^[a-zA-Z0-9~!@#$%^&*()_+\-=[\]{};:,./<>?]+$/.test(passwordValue),
  });

  return requirements;
};

export const extractErrorMessageFromRequirements = (
  requirements: IPasswordRequirement[],
): string | null => {
  return requirements.find((requirement) => !requirement.isMet)?.text ?? null;
};

/**
 * HOOKS
 */

const usePasswordRequirements = (
  passwordValue: string,
): {
  requirements: IPasswordRequirement[];
  isValid: boolean;
  inputErrorMessage: string | null;
} => {
  const { t } = useTranslation();

  const requirements = useMemo<IPasswordRequirement[]>(() => {
    return calculateIfPasswordMeetsRequirements(passwordValue, t);
  }, [t, passwordValue]);

  // grab the first not-met requirement and return a str for the input error message
  const inputErrorMessage = useMemo(() => {
    return extractErrorMessageFromRequirements(requirements);
  }, [requirements]);

  const isValid = useMemo(() => {
    return requirements.every((requirement) => requirement.isMet);
  }, [requirements]);

  return useMemo(
    () => ({ requirements, isValid, inputErrorMessage }),
    [requirements, isValid, inputErrorMessage],
  );
};

/**
 * UI
 *
 */

const PasswordRequirementListItem = React.memo<{
  requirement: IPasswordRequirement;
  shouldDisplayColors: boolean;
  idx: number;
}>((props) => {
  const { isMet, text } = props.requirement;

  const className = useMemo(() => {
    if (!props.shouldDisplayColors) {
      return "";
    }

    if (isMet) {
      return css["password-requirement-list-item-met"];
    }

    return css["password-requirement-list-not-met"];
  }, [isMet, props.shouldDisplayColors]);

  return (
    <li className={className} data-testid={`password-requirement-${props.idx}`}>
      {text}
    </li>
  );
});

PasswordRequirementListItem.displayName = "PasswordRequirementListItem";

export const PasswordRequirements: React.FC<{
  passwordValue: string;
  shouldDisplayColors: boolean;
  containerClassName?: string;
}> = (props) => {
  const { t } = useTranslation();

  const { requirements } = usePasswordRequirements(props.passwordValue);

  return (
    <Text
      component="div"
      className={clsx(
        css["password-requirements-container"],
        props.containerClassName,
      )}
    >
      {t("startPage.inputs.requirements0")}
      <ul>
        {requirements.map((requirement, index) => (
          <PasswordRequirementListItem
            key={index}
            idx={index}
            requirement={requirement}
            shouldDisplayColors={props.shouldDisplayColors}
          />
        ))}
      </ul>
    </Text>
  );
};
